import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store/store";
import {
  requestPhoneOtp,
  verifyPhoneOtp,
} from "../store/slice/authThunks";
import { useNavigate } from "react-router-dom";

const PhoneLogin: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { loading, error, token } = useSelector(
    (state: RootState) => state.auth
  );

  const [step, setStep] = useState<"phone" | "otp">("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (token) {
      navigate("/");
    }
  }, [token, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    try {
      await dispatch(requestPhoneOtp(phone) as any);
      setStep("otp");
      setResendCooldown(60);
    } catch (err: any) {
      setLocalError(err.message || "Failed to send verification code");
    }
  };

  const handleResendOtp = async () => {
    setLocalError(null);
    try {
      await dispatch(requestPhoneOtp(phone) as any);
      setResendCooldown(60);
    } catch (err: any) {
      setLocalError(err.message || "Failed to resend verification code");
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    try {
      await dispatch(verifyPhoneOtp(phone, otp) as any);
    } catch (err: any) {
      setLocalError(err.message || "Invalid or expired code");
    }
  };

  return (
    <section className="bg-gray-50 dark:bg-gray-900 min-h-full flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md bg-white rounded-lg shadow dark:bg-gray-800 dark:border dark:border-gray-700 overflow-y-auto max-h-[95vh]">
        <div className="p-6 sm:p-8 space-y-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white text-center">
            Continue with your phone
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center -mt-4">
            We'll log you in if you already have an account, or create one if
            you're new.
          </p>

          {(localError || error) && (
            <p className="text-red-600 text-center">{localError || error}</p>
          )}
          {token && (
            <p className="text-green-600 text-center">Login successful!</p>
          )}

          {step === "phone" && (
            <form onSubmit={handleRequestOtp} className="space-y-5">
              <div>
                <label
                  htmlFor="phone"
                  className="block mb-1 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Phone number
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+2348012345678"
                  required
                  className="w-full p-2.5 text-sm rounded-lg border border-gray-300 bg-gray-50 text-gray-900 focus:ring-green-600 focus:border-green-600 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-2.5 px-5 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-4 ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700 focus:ring-green-300 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800"
                }`}
              >
                {loading ? "Sending code..." : "Send verification code"}
              </button>
            </form>
          )}

          {step === "otp" && (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                Enter the code sent to {phone}
              </p>
              <div>
                <label
                  htmlFor="otp"
                  className="block mb-1 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Verification code
                </label>
                <input
                  type="text"
                  id="otp"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="123456"
                  required
                  className="w-full p-2.5 text-sm rounded-lg border border-gray-300 bg-gray-50 text-gray-900 focus:ring-green-600 focus:border-green-600 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-2.5 px-5 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-4 ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700 focus:ring-green-300 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800"
                }`}
              >
                {loading ? "Verifying..." : "Verify and continue"}
              </button>
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={loading || resendCooldown > 0}
                className="w-full text-sm font-medium text-green-600 hover:underline dark:text-green-400 disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
              >
                {resendCooldown > 0
                  ? `Resend code in ${resendCooldown}s`
                  : "Resend code"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep("phone");
                  setOtp("");
                  setLocalError(null);
                  setResendCooldown(0);
                }}
                className="w-full text-sm text-gray-500 hover:underline dark:text-gray-400"
              >
                Use a different phone number
              </button>
            </form>
          )}

          <p className="text-sm text-center text-gray-500 dark:text-gray-400">
            Prefer email?{" "}
            <a
              href="/login"
              className="font-medium text-green-600 hover:underline dark:text-green-400"
            >
              Log in with email instead
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default PhoneLogin;
